/**
 * A group object represents a community space where site members can connect and share content.
 * You can create groups and manage their visibility, settings, and metadata.
 * Learn more about [groups](https://support.wix.com/en/article/wix-groups-about-groups).
 */
export interface Group {
  /**
   * Group ID.
   * @readonly
   * @format GUID
   */
  id?: string | null;
  /**
   * A unique part of a group's URL, for example `https:/example.com/groups/slug`.
   * @minLength 1
   * @maxLength 100
   */
  slug?: string | null;
  /** Group privacy status. */
  privacyStatus?: PrivacyStatusWithLiterals;
  /**
   * Group name.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Group description in [DraftJS](https://draftjs.org) format.
   * @maxLength 20480
   */
  description?: string | null;
  /**
   * Group teaser.
   * @maxLength 1000
   */
  teaser?: string | null;
  /**
   * What group members are called, for example `Coworkers`, `Friends`, or `Students`.
   * @minLength 1
   * @maxLength 1000
   */
  memberTitle?: string | null;
  /** Cover image. You cannot upload your own cover image. */
  coverImage?: CoverImage;
  /**
   * Group specific settings.
   *
   * These settings can also be found in [a site's Dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fwix-groups/settings?).
   */
  settings?: GroupSettings;
  /**
   * Total count of current group members.
   * @readonly
   */
  membersCount?: number | null;
  /**
   * Group owner.
   * @readonly
   * @format GUID
   */
  ownerId?: string | null;
  /**
   * Group creation date and time.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time of the latest group update.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Date and time of the most recent group activity, for example a post or comment.
   * @readonly
   */
  lastActivityDate?: Date | null;
}

export enum Type {
  UNKNOWN = 'UNKNOWN',
  ADMIN_APPROVAL = 'ADMIN_APPROVAL',
  PAID_PLANS = 'PAID_PLANS',
  EVENTS = 'EVENTS',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'ADMIN_APPROVAL'
  | 'PAID_PLANS'
  | 'EVENTS';

export interface Events {
  /** @format GUID */
  eventIds?: string[];
}

export interface Logo {
  /**
   * Logo image ID (for internal use).
   * @maxLength 1000
   */
  mediaId?: string | null;
  /** Logo image width. */
  width?: number | null;
  /** Logo image height. */
  height?: number | null;
  /**
   * Alternative text.
   * @maxLength 200
   */
  altText?: string | null;
}

export interface GroupDetailsPosition {
  /** horizontal coordinate */
  x?: number;
  /** vertical coordinate */
  y?: number;
}

export interface Image {
  /**
   * Image ID (for internal use).
   * @maxLength 1000
   */
  mediaId?: string | null;
  /** Image width. */
  width?: number | null;
  /** Image height. */
  height?: number | null;
  /** Indicates pre-configured/auto-generated images (from templates, client generated). */
  preset?: boolean | null;
}

export interface Position {
  /** horizontal coordinate */
  x?: number;
  /** vertical coordinate */
  y?: number;
}

export enum AllowPolicy {
  UNKNOWN = 'UNKNOWN',
  OWNER_AND_ADMINS = 'OWNER_AND_ADMINS',
  OWNER = 'OWNER',
  ALL_MEMBERS = 'ALL_MEMBERS',
}

/** @enumType */
export type AllowPolicyWithLiterals =
  | AllowPolicy
  | 'UNKNOWN'
  | 'OWNER_AND_ADMINS'
  | 'OWNER'
  | 'ALL_MEMBERS';

export interface OnboardingStepSettings {
  stepKey?: StepKeyWithLiterals;
  visible?: boolean;
}

export enum StepKey {
  UNKNOWN = 'UNKNOWN',
  CREATE_POST = 'CREATE_POST',
  REACT_TO_POST = 'REACT_TO_POST',
  INVITE_MEMBERS = 'INVITE_MEMBERS',
}

/** @enumType */
export type StepKeyWithLiterals =
  | StepKey
  | 'UNKNOWN'
  | 'CREATE_POST'
  | 'REACT_TO_POST'
  | 'INVITE_MEMBERS';

export enum PrivacyStatus {
  /** Undefined group privacy status. */
  UNKNOWN = 'UNKNOWN',
  /** Anyone can see the group and its content. Anyone can join the group. */
  PUBLIC = 'PUBLIC',
  /** Anyone can see the group, but only members can see its content. New members must submit a `Join Group Request`. */
  PRIVATE = 'PRIVATE',
  /** Only admins and members can see the group. New members can only be added by other members. */
  SECRET = 'SECRET',
}

/** @enumType */
export type PrivacyStatusWithLiterals =
  | PrivacyStatus
  | 'UNKNOWN'
  | 'PUBLIC'
  | 'PRIVATE'
  | 'SECRET';

export interface AccessRestriction extends AccessRestrictionDataOneOf {
  events?: Events;
  type?: TypeWithLiterals;
}

/** @oneof */
export interface AccessRestrictionDataOneOf {
  events?: Events;
}

export interface GroupDetails {
  /** Group logo. You cannot upload your own logo. */
  logo?: Logo;
  /**
   * What group members are called, for example `Coworkers`, `Friends`, or `Students`.
   * @minLength 1
   * @maxLength 1000
   */
  membersTitle?: string | null;
}

/** Cover image. You cannot upload your own cover image. */
export interface CoverImage {
  /** Cover image. */
  image?: Image;
  /** Position of the corner of the cover image (or logo). */
  position?: Position;
  /** Position of the corner of the cover image (or logo) for mobile browser. */
  mobilePosition?: Position;
  /**
   * Alternative text is typically a relatively short phrase that describes what the image depicts.
   *
   * The alternative text is used:
   * + If the browser cannot display the image.
   * + If the user is utilizing a screen reader.
   * + By search engines to understand what images are on your site.
   * @maxLength 200
   */
  altText?: string | null;
}

export interface GroupSettings {
  /**
   * __Deprecated.__ Use `allowedToInviteMembers` instead.
   * Whether regular members are permitted to invite new members.
   * If `false`, only admins can invite members. Defaults to `true`.
   * @deprecated
   */
  membersCanInvite?: boolean | null;
  /**
   * __Deprecated.__ Use `allowedToApproveJoinRequests` instead.
   * Whether all group members are permitted to approve join group requests.
   * If `false`, member approval is limited to the admins.
   * @deprecated
   */
  membersCanApprove?: boolean | null;
  /** Whether a daily post about new members is enabled. */
  welcomeMemberPostEnabled?: boolean | null;
  /** Whether an automatic post about changing the group details is enabled. */
  groupDetailsChangedPostEnabled?: boolean | null;
  /** Whether all members can see the full member list. */
  showMemberList?: boolean | null;
  /** Determines who can invite members to the group */
  allowedToInviteMembers?: AllowPolicyWithLiterals;
  /** Determines who can approve member join requests to the group */
  allowedToApproveJoinRequests?: AllowPolicyWithLiterals;
  /**
   * Whether AI spam protection is enabled for post creation in the group.
   * If not explicitly set, the default behavior is 'true' (enabled).
   */
  aiSpamProtectionEnabled?: boolean | null;
}

export interface Identity {
  /**
   * Member ID of the group creator.  See the Members API for more details.
   * @format GUID
   */
  id?: string | null;
  identityType?: IdentityTypeWithLiterals;
}

export enum IdentityType {
  /** Wix user. */
  USER = 'USER',
  /** Wix member. */
  MEMBER = 'MEMBER',
}

/** @enumType */
export type IdentityTypeWithLiterals = IdentityType | 'USER' | 'MEMBER';

export interface CreateGroupRequest {
  /** Group to create. */
  group: Group;
  /**
   * ID of the member who created the group, from the Members API. This member will automatically become an admin.
   * @format GUID
   */
  creatorId?: string | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export enum ContentType {
  PLAIN_TEXT = 'PLAIN_TEXT',
  DRAFTJS = 'DRAFTJS',
  RICH_CONTENT = 'RICH_CONTENT',
}

/** @enumType */
export type ContentTypeWithLiterals =
  | ContentType
  | 'PLAIN_TEXT'
  | 'DRAFTJS'
  | 'RICH_CONTENT';

export interface CreateGroupResponse {
  /** Created group. */
  group?: Group;
}

export interface UpdateGroupRequest {
  /** Group to update. */
  group: Group;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface UpdateGroupResponse {
  /** Updated group. */
  group?: Group;
}

export interface GroupCoverChanged {
  /** Old URL of group cover. */
  oldUrl?: string | null;
  /** New URL of group cover. */
  newUrl?: string | null;
}

export interface GroupDescriptionChanged {
  /** Group's old description. */
  oldDescription?: string | null;
  /** Group's new description. */
  newDescription?: string | null;
}

export interface DeleteGroupRequest {
  /**
   * ID of the group to delete.
   * @format GUID
   */
  groupId: string;
}

export interface DeleteGroupResponse {
  /** Deleted group. */
  group?: Group;
}

export interface GetGroupRequest {
  /**
   * ID of the group to retrieve.
   * @format GUID
   */
  groupId: string;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface GetGroupResponse {
  /** Retrieved group. */
  group?: Group;
}

export interface GetGroupBySlugRequest {
  /**
   * Unique part of the group's URL. For example, if a group's URL is `https:/example.com/groups/{my-fitness-group}`, the slug is `my-fitness-group`. Case-sensitive.
   * @minLength 1
   * @maxLength 100
   */
  slug: string;
  /** @format GUID */
  autoInviteId?: string | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface GetGroupBySlugResponse {
  /** Retrieved group. */
  group?: Group;
}

export interface GetGroupIdBySlugRequest {
  /**
   * Unique part of the group's URL, for example `group-1` in `https:/example.com/groups/group-1`. Pass only the slug. Case-sensitive.
   * @minLength 1
   * @maxLength 100
   */
  slug?: string;
  /** @format GUID */
  autoInviteId?: string | null;
}

export interface GetGroupIdBySlugResponse {
  /**
   * ID of the group that correspond to slug
   * @format GUID
   */
  groupId?: string;
  /** Global feed permissions */
  accessPermissions?: GlobalFeedPermissions;
}

export interface GlobalFeedPermissions {
  feedPermissions?: FeedPermissions;
  ownFeedItemPermissions?: FeedItemPermissions;
  allFeedItemPermissions?: FeedItemPermissions;
  activityFeedItemPermissions?: FeedItemPermissions;
}

export interface FeedPermissions {
  canViewPosts?: boolean;
  canCreatePosts?: boolean;
  canPinPosts?: boolean;
  canCreateTopic?: boolean;
  canFollowPosts?: boolean;
  canCreatePaidPosts?: boolean;
  canReadSettings?: boolean;
  canManageSettings?: boolean;
}

export interface FeedItemPermissions {
  canViewFullPost?: boolean;
  canAssignTopic?: boolean;
  canUnassignTopic?: boolean;
  /** bool canFollow = 3; // it's not related to context token */
  canUpdate?: boolean;
  canDelete?: boolean;
  /** always true */
  canShare?: boolean;
  canAddComment?: boolean;
  canReact?: boolean;
  canPin?: boolean;
}

export interface ListGroupsRequest {
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface ListGroupsResponse {
  /** Retrieved Groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface ListGroupsByUserIdRequest {
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface ListGroupsByUserIdResponse {
  groups?: GroupWithMsId[];
}

/** Retrieved Groups by metasite id */
export interface GroupWithMsId {
  metaSiteId?: string;
  groups?: Group[];
}

export interface QueryGroupsRequest {
  /** Query options. */
  query?: Query;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryGroupsResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface QueryJoinedGroupsRequest {
  /** Query options. */
  query?: Query;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface QueryJoinedGroupsResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface QueryGroupsByMembershipRequest {
  /** Query options. */
  query?: Query;
  membershipStatus?: MembershipStatusWithLiterals;
  /** Filter groups by permissions. */
  permissionsFilter?: GroupPermissions;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export enum MembershipStatus {
  NONE = 'NONE',
  JOINED = 'JOINED',
  PENDING = 'PENDING',
}

/** @enumType */
export type MembershipStatusWithLiterals =
  | MembershipStatus
  | 'NONE'
  | 'JOINED'
  | 'PENDING';

export interface GroupPermissions {
  canCreatePosts?: boolean | null;
}

export interface QueryGroupsByMembershipResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface ListGroupIntegrationsDataRequest {
  /**
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  groupIds?: string[];
}

export interface ListGroupIntegrationsDataResponse {
  /**
   * @minSize 1
   * @maxSize 100
   */
  groupsIntegrationsData?: GroupIntegrationsData[];
}

export interface GroupIntegrationsData {
  /** @format GUID */
  groupId?: string;
  feedItemsCount?: number;
  /** @format GUID */
  topicIds?: string[];
  /** @format GUID */
  eventIds?: string[];
  /** @format GUID */
  connectedPricingPlanIds?: string[];
  /** @format GUID */
  onlineProgramIds?: string[];
}

export interface GetGroupMembersGroupIdsRequest {
  /**
   * ID of the group to retrieve members group ids.
   * @format GUID
   */
  groupId?: string;
}

export interface GetGroupMembersGroupIdsResponse {
  /**
   * All members group id
   * @format GUID
   */
  allMembersGroupId?: string;
  /**
   * Admins members group id
   * @format GUID
   */
  adminMembersGroupId?: string;
}

export interface GetGroupBMFeaturesRequest {
  /**
   * ID of the group to retrieve BM features.
   * @format GUID
   */
  groupId?: string;
}

export interface GetGroupBMFeaturesResponse {
  level?: number;
  features?: BMFeatures;
}

export interface BMFeatures {
  /** @maxSize 10 */
  bmFeatures?: BMFeatureWithLiterals[];
}

export enum BMFeature {
  BM_FEATURES_UNKNOWN = 'BM_FEATURES_UNKNOWN',
  BM_FEATURES_GENERATE_TOPICS = 'BM_FEATURES_GENERATE_TOPICS',
  BM_FEATURES_GENERATE_QUESTIONS = 'BM_FEATURES_GENERATE_QUESTIONS',
}

/** @enumType */
export type BMFeatureWithLiterals =
  | BMFeature
  | 'BM_FEATURES_UNKNOWN'
  | 'BM_FEATURES_GENERATE_TOPICS'
  | 'BM_FEATURES_GENERATE_QUESTIONS';

export interface QueryJoinedGroupsWithMemberRequest {
  /** Query options. */
  query?: Query;
  /**
   * ID of the member to find common groups with
   * @format GUID
   */
  memberId?: string;
  /** Content type. */
  contentType?: ContentTypeWithLiterals;
}

export interface QueryJoinedGroupsWithMemberResponse {
  /** Retrieved groups. */
  groups?: Group[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface GetPrivacyRequest {
  /**
   * Group ids to list privacy for
   * @format GUID
   * @maxSize 100
   */
  groupIds?: string[];
}

export interface GetPrivacyResponse {
  /** Privacy statuses listed per group id */
  privacyStatuses?: Record<string, PrivacyStatus>;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
